import { PointerEvent } from 'react';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import CancelOutlinedButton from '@/components/common/buttons/CancelOutlinedButton';
import { HStack, Input, Textarea, VStack } from '@chakra-ui/react';
import { CriticalSystem } from '@/types/CriticalInfo';
import { Field } from '@/components/ui/field';
import { CriticalSystemField, isSystemDocumented } from '@/utils/criticalInfoUtils';
import { Title } from '@/components/ui/title';

interface SystemFormProps {
	system: CriticalSystem;
	heading?: string;
	onUpdateField: (systemName: string, fieldName: string, value: string) => void;
	hasUnsavedChanges?: boolean;
	isSaving?: boolean;
	onSave?: () => void;
	onCancel?: () => void;
	onRemove?: () => void;
	onDone?: () => void;
	readOnly?: boolean;
}

const FIELD_LABELS: Record<string, string> = {
	[CriticalSystemField.Location]: 'Location',
	[CriticalSystemField.ToolNeeded]: 'Tool Required',
	[CriticalSystemField.HowToOperate]: 'How to Operate',
	[CriticalSystemField.Notes]: 'Additional Notes',
};

const FIELD_PLACEHOLDERS: Record<string, string> = {
	[CriticalSystemField.Location]: 'Where is it?',
	[CriticalSystemField.ToolNeeded]: 'What tool is needed?',
	[CriticalSystemField.HowToOperate]: 'How do you operate or shut it off?',
	[CriticalSystemField.Notes]: 'Anything else someone should know?',
};

const getFieldLabel = (fieldName: string) =>
	FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const getFieldPlaceholder = (fieldName: string) =>
	FIELD_PLACEHOLDERS[fieldName] || `Enter ${getFieldLabel(fieldName).toLowerCase()}...`;

const SystemForm = (props: SystemFormProps) => {
	const {
		system,
		heading,
		onUpdateField,
		hasUnsavedChanges = false,
		isSaving = false,
		onSave,
		onCancel,
		onRemove,
		onDone,
		readOnly = false,
	} = props;
	const showEditActions = !readOnly && !!onSave && !!onCancel;
	const showDoneAction = !readOnly && !!onDone && !showEditActions;
	const showFormActions = showEditActions || showDoneAction || !!onRemove;
	// In Done mode, surface a Cancel to back out an accidental addition — but only
	// when no Remove button is already acting as the discard affordance.
	const showDoneCancel = showDoneAction && !!onCancel && !onRemove;
	const doneHasCompanion = showDoneCancel || !!onRemove;

	const handleFieldChange = (fieldName: string, value: string) => {
		if (readOnly) return;
		onUpdateField(system.name, fieldName, value);
	};

	const handleCancelPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
		if (!onCancel || isSaving) return;
		event.preventDefault();
		onCancel();
	};

	return (
		<VStack gap={4} align="stretch">
			{heading && <Title>{heading}</Title>}
			{system.fields.map((field) => (
				<Field key={field.name} label={getFieldLabel(field.name)}>
					{field.name === CriticalSystemField.Notes ? (
						<Textarea
							value={field.value}
							onChange={(event) => handleFieldChange(field.name, event.target.value)}
							placeholder={getFieldPlaceholder(field.name)}
							readOnly={readOnly}
							bg="surface.default"
							textStyle="body"
							borderRadius="16px"
							resize="none"
							rows={3}
						/>
					) : (
						<Input
							value={field.value}
							onChange={(event) => handleFieldChange(field.name, event.target.value)}
							placeholder={getFieldPlaceholder(field.name)}
							readOnly={readOnly}
							bg="surface.default"
							textStyle="body"
							borderRadius="full"
						/>
					)}
				</Field>
			))}
			{showFormActions && (
				<HStack gap={2} pt={2} w="full" justify={showDoneAction && !doneHasCompanion ? 'flex-end' : undefined}>
					{onRemove && (
						<CancelOutlinedButton flex="1" type="button" elevated disabled={isSaving} onClick={onRemove}>
							Remove
						</CancelOutlinedButton>
					)}
					{showEditActions && (
						<>
							<OutlinedButton
								elevated
								flex="1"
								type="button"
								disabled={isSaving}
								onPointerDown={handleCancelPointerDown}
								onClick={onCancel}
							>
								Cancel
							</OutlinedButton>
							<PrimaryButton
								flex="1"
								textStyle="body.sm"
								fontWeight="normal"
								disabled={!hasUnsavedChanges || isSaving}
								loading={isSaving}
								loadingText="Saving..."
								onClick={onSave}
							>
								Save
							</PrimaryButton>
						</>
					)}
					{showDoneAction && (
						<>
							{showDoneCancel && (
								<OutlinedButton
									elevated
									flex="1"
									type="button"
									disabled={isSaving}
									onPointerDown={handleCancelPointerDown}
									onClick={onCancel}
								>
									Cancel
								</OutlinedButton>
							)}
							<OutlinedButton
								elevated
								flex={doneHasCompanion ? '1' : { base: '1', md: '0 0 50%' }}
								type="button"
								textStyle="body.sm"
								fontWeight="medium"
								disabled={isSaving || !isSystemDocumented(system)}
								onClick={onDone}
							>
								Done
							</OutlinedButton>
						</>
					)}
				</HStack>
			)}
		</VStack>
	);
};

export default SystemForm;
